// Practice-mode progress: localStorage I/O for the 10-puzzle curated practice
// set. Mirrors the daily-game persistence layer in `persistence.ts` but kept
// strictly separate so practice play never touches the daily blob, lifetime
// stats, or streak.
//
// Storage shape (key `letterflick:practice`):
//   {
//     schemaVersion: 1,
//     completed: { "1": { won, undoCount, completedAtIso }, ... }
//   }
//
// Replays of an already-completed puzzle OVERWRITE the previous record (we
// only keep the most-recent completion per puzzle).
//
// Back-compat note: earlier v1 blobs also carried a `highestUnlocked` field
// that gated sequential progression. That gate was removed; the field is now
// ignored on read and dropped on write. No schema-version bump because we
// only removed a field — old blobs still parse fine.
//
// Load defaults: on missing key, parse failure, schemaVersion mismatch, or
// any field validation failure, we return a fresh `{ schemaVersion: 1,
// completed: {} }`. Practice progress is non-critical — losing it shouldn't
// crash the player's session.

export const PRACTICE_KEY = 'letterflick:practice' as const
export const PRACTICE_SCHEMA_VERSION = 1 as const

// Total number of curated practice puzzles. Hard-coded here (NOT looked up
// from the corpus) so the progress helpers stay independent of `practice.ts`
// and avoid a circular dependency.
export const PRACTICE_PUZZLE_COUNT = 10 as const

export interface PracticeCompletionRecord {
  readonly won: boolean
  readonly undoCount: number
  // ISO-8601 wall-clock timestamp. Replays overwrite this, so the value is
  // always the LATEST completion (not the first).
  readonly completedAtIso: string
}

export interface PracticeProgress {
  readonly schemaVersion: 1
  // Keyed by puzzle number as a string (JSON-native). Use the helpers below
  // rather than reading this map directly so the key format stays consistent.
  readonly completed: Readonly<Record<string, PracticeCompletionRecord>>
}

function emptyProgress(): PracticeProgress {
  return {
    schemaVersion: PRACTICE_SCHEMA_VERSION,
    completed: {},
  }
}

export function loadPracticeProgress(): PracticeProgress {
  let raw: string | null = null
  try {
    raw = localStorage.getItem(PRACTICE_KEY)
  } catch {
    // Private-mode / sandboxed contexts can throw on read. Treat as missing.
    return emptyProgress()
  }
  if (raw === null) return emptyProgress()

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return emptyProgress()
  }
  if (parsed === null || typeof parsed !== 'object') return emptyProgress()
  const obj = parsed as Record<string, unknown>
  if (obj['schemaVersion'] !== PRACTICE_SCHEMA_VERSION) return emptyProgress()

  const completedRaw = obj['completed']
  if (
    completedRaw === null ||
    typeof completedRaw !== 'object' ||
    Array.isArray(completedRaw)
  ) {
    return emptyProgress()
  }
  const completedSrc = completedRaw as Record<string, unknown>
  const completed: Record<string, PracticeCompletionRecord> = {}
  for (const key of Object.keys(completedSrc)) {
    // Keys must parse as integers in [1, PRACTICE_PUZZLE_COUNT].
    const n = Number(key)
    if (!Number.isInteger(n) || n < 1 || n > PRACTICE_PUZZLE_COUNT) {
      return emptyProgress()
    }
    const rec = completedSrc[key]
    if (rec === null || typeof rec !== 'object') return emptyProgress()
    const r = rec as Record<string, unknown>
    if (typeof r['won'] !== 'boolean') return emptyProgress()
    if (typeof r['undoCount'] !== 'number' || !Number.isFinite(r['undoCount'])) {
      return emptyProgress()
    }
    if (typeof r['completedAtIso'] !== 'string') return emptyProgress()
    completed[key] = {
      won: r['won'],
      undoCount: r['undoCount'],
      completedAtIso: r['completedAtIso'],
    }
  }

  return {
    schemaVersion: PRACTICE_SCHEMA_VERSION,
    completed,
  }
}

export function savePracticeProgress(p: PracticeProgress): void {
  try {
    localStorage.setItem(PRACTICE_KEY, JSON.stringify(p))
  } catch (err) {
    // QuotaExceededError, private-mode rejection, etc. Practice progress is
    // best-effort — losing it shouldn't crash the player's session.
    console.warn('Letter Flick: failed to save practice progress', err)
  }
}

// Read-modify-write: record (or overwrite) the completion record for puzzle
// `n`. Returns the new in-memory state for the caller's reference.
export function recordPracticeCompletion(
  n: number,
  record: PracticeCompletionRecord,
): PracticeProgress {
  const current = loadPracticeProgress()
  const completed: Record<string, PracticeCompletionRecord> = {
    ...current.completed,
    [String(n)]: record,
  }
  const next: PracticeProgress = {
    schemaVersion: PRACTICE_SCHEMA_VERSION,
    completed,
  }
  savePracticeProgress(next)
  return next
}
